import { type ChangeEvent, type FC, useRef, useState } from "react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Label } from "@/shared/components/ui/label";
import { Separator } from "@/shared/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { AddressPicker } from "@/shared/components/ui/AddressPicker";
import { EMPTY_ADDRESS } from "@/shared/utils/addressUtils";
import { cn } from "@/shared/utils/core";
import { currencyInput } from "@/shared/utils/currencyInput";
import { useAssetEstimate } from "@/shared/hooks/useAssetEstimate";
import { buildAddress } from "@/shared/utils/addressUtils";

import type { AddressParts } from "@/shared/utils/addressUtils";

const PROPERTY_TYPES = [
  "Single-family home",
  "Condo / Co-op",
  "Townhouse",
  "Multi-family",
  "Land / Lot",
  "Commercial",
  "Other",
];

const OWNERSHIP_OPTIONS = [
  { value: "sole", label: (name: string) => `By ${name} alone` },
  { value: "with-someone", label: () => "With someone else" },
  { value: "in-trust", label: () => "In a trust" },
  { value: "not-sure", label: () => "Not sure" },
] as const;

interface AddPropertyFormValues {
  address: AddressParts;
  propertyType: string;
  estimatedValue: string;
  ownership: string;
}

interface FormErrors {
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  propertyType?: string;
  estimatedValue?: string;
  ownership?: string;
}

function validate(
  address: AddressParts,
  propertyType: string,
  estimatedValue: string,
  ownership: string,
): FormErrors {
  const errors: FormErrors = {};
  if (!address.streetAddress.trim()) errors.streetAddress = "Street address is required";
  if (!address.city.trim()) errors.city = "City is required";
  if (!address.state) errors.state = "State is required";
  if (!address.zipCode.trim()) errors.zipCode = "Zip code is required";
  if (!propertyType) errors.propertyType = "Property type is required";
  if (!estimatedValue.replace(/[^0-9]/g, "")) errors.estimatedValue = "Estimated value is required";
  if (!ownership) errors.ownership = "Select how this property was owned";
  return errors;
}

interface LegalAuthorityPropertyFormProps {
  mode: "add" | "edit";
  personFirstName: string;
  estateId: number;
  portalContainer?: HTMLElement | null;
  initialValues?: AddPropertyFormValues;
  onSubmit: (form: AddPropertyFormValues) => void;
  onCancel: () => void;
  onRemove?: () => void;
}

export const LegalAuthorityPropertyForm: FC<LegalAuthorityPropertyFormProps> = ({
  mode,
  personFirstName,
  estateId,
  portalContainer,
  initialValues,
  onSubmit,
  onCancel,
  onRemove,
}) => {
  const [address, setAddress] = useState<AddressParts>(initialValues?.address ?? EMPTY_ADDRESS);
  const [propertyType, setPropertyType] = useState(initialValues?.propertyType ?? "");
  const [estimatedValue, setEstimatedValue] = useState(initialValues?.estimatedValue ?? "");
  const valueInput = currencyInput(estimatedValue, setEstimatedValue);
  const [ownership, setOwnership] = useState(initialValues?.ownership ?? "");
  const [errors, setErrors] = useState<FormErrors>({});
  const estimateMutation = useAssetEstimate();
  const lastEstimatedAddress = useRef("");

  const handleSubmit = () => {
    const next = validate(address, propertyType, estimatedValue, ownership);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({ address, propertyType, estimatedValue, ownership });
  };

  const clearError = (field: keyof FormErrors) => {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const tryEstimate = (addr: AddressParts) => {
    const full = buildAddress(addr);
    if (
      !addr.streetAddress.trim() ||
      !addr.city.trim() ||
      !addr.state ||
      !addr.zipCode.trim() ||
      full === lastEstimatedAddress.current
    )
      return;
    lastEstimatedAddress.current = full;
    estimateMutation.mutate(
      { estateId, assetType: "real-estate", data: { address: full } },
      {
        onSuccess: (result) => {
          if (result.estimatedValue > 0) {
            setEstimatedValue(String(result.estimatedValue));
            clearError("estimatedValue");
          }
          if (result.suggestedItemType && !propertyType) {
            setPropertyType(result.suggestedItemType);
            clearError("propertyType");
          }
        },
      },
    );
  };

  const handleAddressChange = (value: AddressParts) => {
    setAddress(value);
    if (value.streetAddress.trim()) clearError("streetAddress");
    if (value.city.trim()) clearError("city");
    if (value.state) clearError("state");
    if (value.zipCode.trim()) clearError("zipCode");
    tryEstimate(value);
  };

  const handleEstimatedValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    valueInput.handleChange(e);
    clearError("estimatedValue");
  };

  const handleOwnershipChange = (value: string) => {
    setOwnership(value);
    clearError("ownership");
  };

  return (
    <div className="flex flex-col gap-6 rounded-lg border border-brand bg-white p-6">
      <div className="flex flex-col gap-1">
        <p className="text-base font-semibold">
          {mode === "edit" ? "Editing this property" : "Add a property"}
        </p>
        {mode === "add" && (
          <p className="text-sm text-secondary">
            Start with the address. The property type and an estimated value come from public
            records where possible
          </p>
        )}
      </div>

      <AddressPicker
        value={address}
        onChange={handleAddressChange}
        stateFormat="abbreviation"
        labels={{ apartment: "Apartment, suite" }}
        errors={{
          streetAddress: errors.streetAddress,
          city: errors.city,
          state: errors.state,
          zipCode: errors.zipCode,
        }}
        portalContainer={portalContainer}
      />

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label>Property type</Label>
          <Select
            value={propertyType}
            onValueChange={(v) => {
              setPropertyType(v);
              clearError("propertyType");
            }}
          >
            <SelectTrigger
              className={errors.propertyType ? "border-destructive border-[2px]" : undefined}
            >
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError message={errors.propertyType} />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <Label>Estimated value</Label>
            {estimateMutation.isPending && (
              <span className="text-2xs text-secondary animate-pulse">Estimating...</span>
            )}
          </div>
          <CurrencyInput
            value={valueInput.displayValue}
            onChange={handleEstimatedValueChange}
            placeholder="0"
            wrapperClassName={errors.estimatedValue ? "border-destructive border-[2px]" : undefined}
          />
          <FieldError message={errors.estimatedValue} />
          <p className="text-2xs text-tertiary">A rough number is fine for now</p>
        </div>
      </div>

      <Separator />

      <div className="flex flex-col gap-3">
        <p className="text-sm font-medium">How was this property owned?</p>
        <div className="flex flex-wrap items-center gap-3">
          {OWNERSHIP_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => handleOwnershipChange(value)}
              className={cn(
                "rounded-full border px-3 py-2 text-sm",
                ownership === value
                  ? "border-brand bg-brand-subtle font-medium text-brand"
                  : "border-primary bg-white text-primary",
              )}
            >
              {label(personFirstName)}
            </button>
          ))}
        </div>
        <FieldError message={errors.ownership} />
      </div>

      <Separator />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        {onRemove && (
          <Button variant="link" onClick={onRemove}>
            Remove property
          </Button>
        )}
        <div className="flex flex-1 items-center justify-end gap-4">
          <Button variant="outline" size="md" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="brand" size="md" onClick={handleSubmit}>
            {mode === "edit" ? "Save changes" : "Add property"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export type { AddPropertyFormValues };
